import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { courses } from "../assets/data/data";

const siteUrl = "https://www.timbaktu.co.ke";
const defaultImage = `${siteUrl}/timbaktu-preview.jpg`;

const pageMeta = {
  "/": {
    title: "TimBaktu Academy | Digital Skills Training in Kenya",
    description:
      "TimBaktu Academy helps learners build practical digital skills, tech careers, and entrepreneurial confidence through hands-on training in Kenya.",
    keywords:
      "TimBaktu Academy, digital skills Kenya, software development training, digital marketing courses, tech academy Nairobi, learn to code Kenya",
  },
  "/courses": {
    title: "Courses | TimBaktu Academy",
    description:
      "Explore practical courses in software development, digital marketing, design, and youth tech training at TimBaktu Academy.",
    keywords:
      "TimBaktu courses, software development course Kenya, digital marketing training, design courses, tech learning", 
  },
  "/about": {
    title: "About TimBaktu | Mission and Vision",
    description:
      "Learn about TimBaktu Academy, our mission, vision, and commitment to empowering the next generation of African tech talent.",
    keywords: "about TimBaktu, tech academy Kenya, digital skills mission, African tech talent",
  },
  "/why-choose-us": {
    title: "Why Choose Us | TimBaktu Academy",
    description:
      "Discover why learners choose TimBaktu for practical, supportive, and career-focused digital skills training.",
    keywords: "why choose TimBaktu, practical learning, tech training, digital skills support",
  },
  "/blog-events": {
    title: "Blog & Events | TimBaktu Academy",
    description:
      "Read inspiring tech insights and discover upcoming events, workshops, and learning opportunities from TimBaktu Academy.",
    keywords: "TimBaktu blog, tech events Kenya, innovation workshops, digital skills events",
  },
  "/digital-skills-stem": {
    title: "Digital Skills & STEM Education | TimBaktu Academy",
    description:
      "Discover TimBaktu Academy's practical digital skills and STEM education programs that help learners become confident creators and problem-solvers.",
    keywords:
      "digital skills education Kenya, STEM education Kenya, technology for learners, coding and STEM programs, TimBaktu Academy",
  },
  "/contact": {
    title: "Contact TimBaktu Academy",
    description:
      "Get in touch with TimBaktu Academy for admissions, partnerships, and digital skills training support.",
    keywords: "contact TimBaktu, admissions Kenya, tech academy contact, Timbaktu support",
  },
  "/signup": {
    title: "Register Now | TimBaktu Academy",
    description:
      "Register for a TimBaktu Academy course and begin your path to practical digital skills, creativity, and opportunity.",
    keywords: "register TimBaktu, enroll in digital skills training, tech academy registration",
  },
};

const ensureMetaTag = (selector, attributes, isProperty = false) => {
  const tag = document.querySelector(selector) || document.createElement("meta");

  if (!tag.parentNode) {
    document.head.appendChild(tag);
  }

  if (isProperty) {
    tag.setAttribute("property", attributes.name);
  } else {
    tag.setAttribute("name", attributes.name);
  }

  tag.setAttribute("content", attributes.content);

  return tag;
};

const ensureLinkTag = (selector, attributes) => {
  const tag = document.querySelector(selector) || document.createElement("link");

  if (!tag.parentNode) {
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([name, value]) => tag.setAttribute(name, value));

  return tag;
};

const ensureStructuredData = (data) => {
  let script = document.querySelector('script[data-seo="structured-data"]');

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seo = "structured-data";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
};

const Seo = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const courseMatch = pathname.match(/^\/course\/(\d+)$/);
    const course = courseMatch
      ? courses.find((item) => item.id === Number(courseMatch[1]))
      : null;
    const routeMeta = course
      ? {
          title: `${course.title} | TimBaktu Academy`,
          description: course.description.course_desc,
          keywords: `${course.title}, TimBaktu Academy, digital skills training Kenya, online course Kenya`,
          type: "course",
        }
      : pageMeta[pathname] || pageMeta["/"];
    const pageUrl = `${siteUrl}${pathname}`;

    document.title = routeMeta.title;

    ensureMetaTag('meta[name="description"]', {
      name: "description",
      content: routeMeta.description,
    });

    ensureMetaTag('meta[name="keywords"]', {
      name: "keywords",
      content: routeMeta.keywords,
    });

    ensureMetaTag('meta[name="robots"]', {
      name: "robots",
      content: "index, follow",
    });

    ensureMetaTag('meta[property="og:title"]', {
      name: "og:title",
      content: routeMeta.title,
    }, true);

    ensureMetaTag('meta[property="og:description"]', {
      name: "og:description",
      content: routeMeta.description,
    }, true);

    ensureMetaTag('meta[property="og:url"]', {
      name: "og:url",
      content: pageUrl,
    }, true);

    ensureMetaTag('meta[property="og:type"]', {
      name: "og:type",
      content: routeMeta.type || "website",
    }, true);

    ensureMetaTag('meta[property="og:image"]', {
      name: "og:image",
      content: defaultImage,
    }, true);

    ensureMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });

    ensureMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: routeMeta.title,
    });

    ensureMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: routeMeta.description,
    });

    ensureMetaTag('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: defaultImage,
    });

    ensureLinkTag('link[rel="canonical"]', { rel: "canonical", href: pageUrl });

    ensureStructuredData(
      course
        ? {
            "@context": "https://schema.org",
            "@type": "Course",
            name: course.title,
            description: course.description.course_desc,
            url: pageUrl,
            provider: {
              "@type": "Organization",
              name: "TimBaktu Academy",
              url: siteUrl,
            },
          }
        : {
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: "TimBaktu Academy",
            url: siteUrl,
            description: pageMeta["/"].description,
          }
    );
  }, [location]);

  return null;
};

export default Seo;
